import React, { useState, useEffect } from 'react';
import { adminApi } from '../lib/adminAuth';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, DollarSign, Receipt, AlertCircle,
  Calendar, Users, FileText, BarChart3, PieChart, Clock,
  ChevronRight, Download, RefreshCw, Filter, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import EmptyState from './EmptyState';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { formatCurrency } from '../lib/formatters';
import { HeaderSkeleton, MetricCardsSkeleton, TableSkeleton } from './skeletons/Skeletons';

// KPI Card Component
function KPICard({ title, shortTitle, value, icon: Icon, color, trend, subtitle, onClick }) {
  const colorClasses = {
    blue: 'from-[#3366FF] to-[#3366FF]',
    green: 'from-emerald-500 to-emerald-600',
    purple: 'from-purple-500 to-purple-600',
    orange: 'from-orange-500 to-orange-600',
    red: 'from-red-500 to-red-600',
    cyan: 'from-cyan-500 to-cyan-600',
    pink: 'from-[#FF3366] to-[#FF3366]',
    amber: 'from-amber-500 to-amber-600',
  };

  return (
    <div 
      className={`relative overflow-hidden  bg-gradient-to-br ${colorClasses[color]} p-4 sm:p-5 text-white shadow-lg transition-all hover:shadow-xl ${onClick ? 'cursor-pointer hover:scale-[1.02]' : ''}`}
      onClick={onClick}
      data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] sm:text-sm font-medium text-white/80 mb-1 leading-tight">
            <span className="sm:hidden">{shortTitle || title}</span>
            <span className="hidden sm:inline">{title}</span>
          </p>
          <p className="text-base sm:text-2xl font-bold tracking-tight break-all">{value}</p>
          {subtitle && (
            <p className="text-[10px] sm:text-xs text-white/70 mt-1 leading-tight">{subtitle}</p>
          )}
          {trend !== undefined && trend !== null && (
            <div className="flex items-center gap-1 mt-2">
              {trend >= 0 ? (
                <ArrowUpRight className="w-3 h-3 sm:w-4 sm:h-4 text-white/90" />
              ) : (
                <ArrowDownRight className="w-3 h-3 sm:w-4 sm:h-4 text-white/90" />
              )}
              <span className="text-[10px] sm:text-xs font-medium text-white/90">
                {Math.abs(trend)}% vs last period
              </span>
            </div>
          )}
        </div>
        <div className="p-2 sm:p-3 bg-white/20 rounded-xl flex-shrink-0">
          <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
        </div>
      </div>
      <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-white/10 rounded-full" />
    </div>
  );
}

// Mini Bar Chart Component
function MiniBarChart({ data, height = 120 }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  
  if (!data || data.length === 0) return null;
  
  const maxValue = Math.max(...data.map(d => d.revenue || 0), 1);
  const labelHeight = 16;
  const barAreaHeight = height - labelHeight;
  
  return (
    <div className="flex gap-1 relative" style={{ height }} data-testid="revenue-trend-chart">
      {data.map((item, i) => {
        const barHeight = Math.max((item.revenue / maxValue) * barAreaHeight, 2);
        return (
          <div 
            key={i} 
            className="flex-1 flex flex-col items-center justify-end relative"
            onMouseEnter={() => setHoveredIdx(i)}
            onMouseLeave={() => setHoveredIdx(null)}
          >
            {hoveredIdx === i && (
              <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 z-20 bg-gray-900 text-white text-[10px] px-2.5 py-1.5 rounded-xl shadow-lg whitespace-nowrap pointer-events-none" data-testid={`tooltip-${i}`}>
                <div className="font-semibold">{item.month}</div>
                <div className="text-teal-300">{formatCurrency(item.revenue)}</div>
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
              </div>
            )}
            <div 
              className="w-full bg-gradient-to-t from-teal-500 to-teal-400 rounded-t-sm transition-all hover:from-teal-400 hover:to-teal-300 cursor-pointer"
              style={{ height: `${barHeight}px` }}
              data-testid={`bar-${i}`}
            />
            <span className="text-[9px] text-neutral-500 truncate w-full text-center" style={{ height: labelHeight }}>
              {item.month?.slice(0, 3)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// Donut Chart Component (CSS-based)
function DonutChart({ data }) {
  if (!data || data.length === 0) return null;
  
  const total = data.reduce((sum, d) => sum + d.amount, 0);
  if (total === 0) return null;
  
  const colors = ['#14b8a6', '#f59e0b', '#ef4444', '#8b5cf6'];
  let currentAngle = 0;
  
  const segments = data.map((item, i) => {
    const percentage = (item.amount / total) * 100;
    const startAngle = currentAngle;
    currentAngle += (percentage / 100) * 360;
    return { ...item, percentage, startAngle, color: colors[i % colors.length] };
  });

  return (
    <div className="flex items-center gap-4">
      <div className="relative w-32 h-32">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          {segments.map((seg, i) => {
            const radius = 40;
            const circumference = 2 * Math.PI * radius;
            const dashLength = (seg.percentage / 100) * circumference;
            const dashOffset = -(segments.slice(0, i).reduce((sum, s) => sum + (s.percentage / 100) * circumference, 0));
            
            return (
              <circle
                key={i}
                cx="50"
                cy="50"
                r={radius}
                fill="none"
                stroke={seg.color}
                strokeWidth="20"
                strokeDasharray={`${dashLength} ${circumference - dashLength}`}
                strokeDashoffset={dashOffset}
                className="transition-all duration-300"
              />
            );
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-lg font-bold text-[#1a1a2e]">{formatCurrency(total, true)}</span>
          <span className="text-[10px] text-neutral-500">Total</span>
        </div>
      </div>
      <div className="flex-1 space-y-2">
        {segments.map((seg, i) => (
          <div key={i} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: seg.color }} />
              <span className="text-neutral-700">{seg.label}</span>
            </div>
            <span className="font-medium text-[#1a1a2e]">{formatCurrency(seg.amount, true)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Recent Invoices Table
function RecentInvoicesTable({ invoices, onViewAll, onInvoiceClick }) {
  const statusColors = {
    paid: 'bg-emerald-100 text-emerald-700',
    sent: 'bg-blue-100 text-blue-700',
    draft: 'bg-neutral-100 text-neutral-700',
    overdue: 'bg-red-100 text-red-700',
    partially_paid: 'bg-amber-100 text-amber-700'
  };

  return (
    <div className="soft-card overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-white/30">
        <h3 className="font-semibold text-[#1a1a2e] text-sm sm:text-base">Recent Invoices</h3>
        <Button variant="ghost" size="sm" onClick={onViewAll} className="text-teal-600 hover:text-teal-700 text-xs sm:text-sm">
          View All <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
      <div className="divide-y divide-gray-50">
        {invoices?.slice(0, 5).map((inv, i) => (
          <button
            key={i}
            onClick={() => onInvoiceClick?.(inv)}
            className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-teal-50 transition-colors text-left group gap-2"
            data-testid={`recent-invoice-${inv._id}`}
          >
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[#1a1a2e] text-sm group-hover:text-teal-700 truncate">{inv.invoice_number || 'Draft'}</p>
              <p className="text-xs text-neutral-500 truncate">{inv.client_name}</p>
            </div>
            <div className="text-right flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <span className={`px-2 py-1 text-[10px] sm:text-xs font-medium rounded-full whitespace-nowrap ${statusColors[inv.status] || 'bg-neutral-100 text-neutral-600'}`}>
                {inv.status?.replace('_', ' ')}
              </span>
              <span className="font-semibold text-[#1a1a2e] text-xs sm:text-sm whitespace-nowrap">{formatCurrency(inv.amount)}</span>
              <ChevronRight className="w-4 h-4 text-neutral-300 group-hover:text-teal-500 hidden sm:block" />
            </div>
          </button>
        ))}
        {(!invoices || invoices.length === 0) && (
          <EmptyState icon={Receipt} title="No invoices found" size="small" testId="empty-dashboard-invoices" />
        )}
      </div>
    </div>
  );
}

// Top Clients Table
function TopClientsTable({ clients, onClientClick }) {
  return (
    <div className="soft-card overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-white/30">
        <h3 className="font-semibold text-[#1a1a2e]">Top Clients by Base Revenue</h3>
        <Users className="w-5 h-5 text-neutral-400" />
      </div>
      <div className="divide-y divide-gray-50">
        {clients?.slice(0, 5).map((client, i) => (
          <button
            key={i}
            onClick={() => onClientClick?.(client)}
            className="w-full flex items-center justify-between p-4 hover:bg-teal-50 transition-colors text-left group"
            data-testid={`top-client-${client.client_id || client.id || i}`}
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-semibold text-sm">
                {client.name?.charAt(0) || '?'}
              </div>
              <div>
                <p className="font-medium text-[#1a1a2e] text-sm group-hover:text-teal-700">{client.name}</p>
                <p className="text-xs text-neutral-500">
                  {client.outstanding > 0 ? `Outstanding: ${formatCurrency(client.outstanding)}` : 'No dues'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-[#1a1a2e]">{formatCurrency(client.revenue)}</span>
              <ChevronRight className="w-4 h-4 text-neutral-300 group-hover:text-teal-500" />
            </div>
          </button>
        ))}
        {(!clients || clients.length === 0) && (
          <div className="p-6 text-center text-neutral-500 text-sm">
            <Users className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
            No client data available
          </div>
        )}
      </div>
    </div>
  );
}

// PO Spend by Category — horizontal bar breakdown of purchase-order spend
// grouped by the mandatory PO category tag. Uses the /spend-by-category
// endpoint and gracefully hides itself when there's nothing to show.
function POSpendByCategoryCard({ payload, months, onMonthsChange, onNavigate }) {
  const rows = (payload?.breakdown || []).slice(0, 8);
  const grandTotal = payload?.grand_total || 0;
  const palette = [
    'bg-fuchsia-500', 'bg-teal-500', 'bg-amber-500', 'bg-blue-500',
    'bg-emerald-500', 'bg-rose-500', 'bg-indigo-500', 'bg-orange-500',
  ];

  return (
    <div className="glass-card p-5" data-testid="po-spend-by-category">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="font-semibold text-[#1a1a2e]">PO Spend by Category</h3>
          <p className="text-xs text-neutral-500">Where purchase-order money is going · {formatCurrency(grandTotal)} across {payload?.breakdown?.length || 0} tags</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(months)} onValueChange={(v) => onMonthsChange(Number(v))}>
            <SelectTrigger className="w-36" data-testid="po-spend-months-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last 30 days</SelectItem>
              <SelectItem value="3">Last 3 months</SelectItem>
              <SelectItem value="6">Last 6 months</SelectItem>
              <SelectItem value="12">Last 12 months</SelectItem>
              <SelectItem value="24">Last 24 months</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-neutral-500">
          <PieChart className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
          No purchase-order spend in the selected window.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row, i) => (
            <button
              key={row.category}
              type="button"
              onClick={() => onNavigate?.('purchase_orders', row.category)}
              className="w-full text-left group"
              data-testid={`po-spend-cat-${row.category.replace(/\s+/g, '-').toLowerCase()}`}
            >
              <div className="flex justify-between items-baseline mb-1 text-sm">
                <span className="font-medium text-neutral-800 truncate mr-3 group-hover:text-fuchsia-600 transition-colors">
                  {row.category}
                </span>
                <span className="text-neutral-600 whitespace-nowrap">
                  <span className="font-semibold text-[#1a1a2e]">{formatCurrency(row.total)}</span>
                  <span className="text-xs text-neutral-400 ml-2">{row.count} PO{row.count !== 1 ? 's' : ''} · {row.share}%</span>
                </span>
              </div>
              <div className="h-2 rounded-full bg-neutral-100 overflow-hidden">
                <div
                  className={`h-full ${palette[i % palette.length]} transition-all group-hover:brightness-110`}
                  style={{ width: `${Math.max(row.share, 2)}%` }}
                />
              </div>
            </button>
          ))}
          {(payload?.breakdown?.length || 0) > rows.length && (
            <p className="text-xs text-neutral-400 pt-1 text-center">
              + {payload.breakdown.length - rows.length} more categor{payload.breakdown.length - rows.length === 1 ? 'y' : 'ies'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// Main Dashboard Component
export default function AccountingDashboard({ onNavigate }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('ytd');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [error, setError] = useState(null);
  const [categorySpend, setCategorySpend] = useState(null);
  const [categoryMonths, setCategoryMonths] = useState(12);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      let url = `/api/admin/accounting/dashboard/enhanced?period=${period}`;
      if (period === 'custom' && customFrom && customTo) {
        url += `&from_date=${customFrom}&to_date=${customTo}`;
      }
      const response = await adminApi.get(url);
      setData(response.data);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(err.response?.data?.detail || 'Failed to load dashboard');
      toast.error('Failed to load accounting dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategorySpend = async (months) => {
    try {
      const res = await adminApi.get(`/api/admin/accounting/purchase-orders/spend-by-category?months=${months}`);
      setCategorySpend(res.data);
    } catch (err) {
      // Non-fatal — hide the card if the caller lacks permission
      if (err.response?.status !== 403) {
        console.error('Category spend fetch error:', err);
      }
      setCategorySpend(null);
    }
  };

  useEffect(() => {
    if (period !== 'custom' || (customFrom && customTo)) {
      fetchDashboard();
    }
  }, [period, customFrom, customTo]);

  useEffect(() => {
    fetchCategorySpend(categoryMonths);
  }, [categoryMonths]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="accounting-dashboard-loading">
        <HeaderSkeleton />
        <MetricCardsSkeleton count={4} />
        <TableSkeleton rows={6} columns={5} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-red-600 font-medium">{error}</p>
          <Button onClick={fetchDashboard} className="mt-4">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  const kpis = data?.kpis || {};
  const revenueTrend = data?.revenue_trend || [];
  const receivablesByAge = data?.receivables_by_age || [];
  const topClients = data?.top_clients || [];
  const recentInvoices = data?.recent_invoices || [];
  const invoiceStats = data?.invoice_stats || {};
  const gstSummary = data?.gst_summary || {};

  return (
    <div className="space-y-6" data-testid="accounting-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a2e]">Financial Dashboard</h1>
          <p className="text-sm text-neutral-500 mt-1">
            {data?.period?.type === 'ytd' ? 'Current Financial Year to Date' : 
             data?.period?.type === 'cy' ? 'Current Calendar Year' :
             data?.period?.type === 'mtd' ? 'Month to Date' :
             data?.period?.type === 'qtd' ? 'Quarter to Date' :
             data?.period?.type === 'last_month' ? 'Last Month' :
             data?.period?.type === 'last_quarter' ? 'Last Quarter' :
             data?.period?.type === 'last_fy' ? 'Previous Financial Year' :
             data?.period?.type === 'all_time' ? 'All Time' :
             `${data?.period?.from} to ${data?.period?.to}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-full sm:w-52" data-testid="dashboard-period-select">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ytd">Current FY (Apr–Mar)</SelectItem>
              <SelectItem value="cy">Current Calendar Year</SelectItem>
              <SelectItem value="mtd">This Month</SelectItem>
              <SelectItem value="qtd">This Quarter</SelectItem>
              <SelectItem value="last_month">Last Month</SelectItem>
              <SelectItem value="last_quarter">Last Quarter</SelectItem>
              <SelectItem value="last_fy">Last FY</SelectItem>
              <SelectItem value="all_time">All Time</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
          {period === 'custom' && (
            <div className="flex items-center gap-2">
              <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-9 w-36" />
              <span className="text-neutral-400 text-sm">to</span>
              <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-9 w-36" />
            </div>
          )}
          <Button variant="outline" size="icon" onClick={fetchDashboard} title="Refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* KPI Cards - 6 Precise Financial Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <KPICard
          title="Total Invoice Value"
          shortTitle="Invoice Value"
          value={formatCurrency(kpis.total_invoice_value)}
          icon={FileText}
          color="blue"
          subtitle="Base + GST (before TDS)"
          onClick={() => onNavigate?.('invoices')}
        />
        <KPICard
          title="Total Receivable"
          shortTitle="Receivable"
          value={formatCurrency(kpis.total_receivable)}
          icon={Receipt}
          color="purple"
          subtitle="After TDS deduction"
          onClick={() => onNavigate?.('invoices')}
        />
        <KPICard
          title="Net Receivable"
          shortTitle="Net Recv."
          value={formatCurrency(kpis.net_receivable)}
          icon={DollarSign}
          color="cyan"
          subtitle="Excl. GST & TDS"
          onClick={() => onNavigate?.('reports', 'revenue')}
        />
        <KPICard
          title="GST Receivable"
          shortTitle="GST Recv."
          value={formatCurrency(kpis.gst_receivable)}
          icon={ArrowUpRight}
          color="orange"
          subtitle="Total GST component"
        />
        <KPICard
          title="Total Received"
          shortTitle="Received"
          value={formatCurrency(kpis.total_received)}
          icon={TrendingUp}
          color="green"
          subtitle={`${invoiceStats.paid || 0} paid invoices`}
          onClick={() => onNavigate?.('payments')}
        />
        <KPICard
          title="Outstanding Receivable"
          shortTitle="Outstanding"
          value={formatCurrency(kpis.outstanding_receivable)}
          icon={Clock}
          color="red"
          subtitle={`${invoiceStats.pending || 0} pending, ${invoiceStats.overdue || 0} overdue`}
          onClick={() => onNavigate?.('reports', 'aged-receivables')}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trend */}
        <div className="glass-card p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-[#1a1a2e]">Revenue Trend</h3>
              <p className="text-xs text-neutral-500">Base revenue (excl. GST & TDS) — Last 12 months</p>
            </div>
            <BarChart3 className="w-5 h-5 text-neutral-400" />
          </div>
          <div className="h-40">
            <MiniBarChart data={revenueTrend} height={160} />
          </div>
        </div>

        {/* Receivables by Age */}
        <div className="glass-card p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-[#1a1a2e]">Receivables by Age</h3>
              <p className="text-xs text-neutral-500">Aging analysis</p>
            </div>
            <PieChart className="w-5 h-5 text-neutral-400" />
          </div>
          <DonutChart data={receivablesByAge} />
        </div>
      </div>

      {/* GST Summary */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-5 text-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">GST Summary</h3>
          <Receipt className="w-5 h-5 text-white/60" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4">
          <div>
            <p className="text-xs text-white/60 mb-1">CGST</p>
            <p className="text-sm sm:text-lg font-semibold">{formatCurrency(gstSummary.total_cgst)}</p>
          </div>
          <div>
            <p className="text-xs text-white/60 mb-1">SGST</p>
            <p className="text-sm sm:text-lg font-semibold">{formatCurrency(gstSummary.total_sgst)}</p>
          </div>
          <div>
            <p className="text-xs text-white/60 mb-1">IGST</p>
            <p className="text-sm sm:text-lg font-semibold">{formatCurrency(gstSummary.total_igst)}</p>
          </div>
          <div>
            <p className="text-xs text-white/60 mb-1">Total GST</p>
            <p className="text-sm sm:text-lg font-semibold text-teal-400">{formatCurrency(gstSummary.total_gst)}</p>
          </div>
          <div>
            <p className="text-xs text-white/60 mb-1">TDS Deducted</p>
            <p className="text-sm sm:text-lg font-semibold text-amber-400">{formatCurrency(gstSummary.total_tds)}</p>
          </div>
        </div>
      </div>

      {/* PO Spend by Category */}
      <POSpendByCategoryCard
        payload={categorySpend}
        months={categoryMonths}
        onMonthsChange={setCategoryMonths}
        onNavigate={onNavigate}
      />

      {/* Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentInvoicesTable 
          invoices={recentInvoices} 
          onViewAll={() => onNavigate?.('invoices')}
          onInvoiceClick={(inv) => onNavigate?.('invoices', inv._id)}
        />
        <TopClientsTable 
          clients={topClients}
          onClientClick={(client) => onNavigate?.('reports', `client-statement:${client.client_id || client.id}`)}
        />
      </div>

      {/* Invoice Stats */}
      <div className="glass-card p-5">
        <h3 className="font-semibold text-[#1a1a2e] mb-4">Invoice Status Breakdown</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4">
          <div className="text-center p-3 bg-white/40 rounded-xl">
            <p className="text-xl sm:text-2xl font-bold text-[#1a1a2e]">{invoiceStats.total || 0}</p>
            <p className="text-[10px] sm:text-xs text-neutral-500">Total</p>
          </div>
          <div className="text-center p-3 bg-emerald-50 rounded-xl">
            <p className="text-xl sm:text-2xl font-bold text-emerald-600">{invoiceStats.paid || 0}</p>
            <p className="text-[10px] sm:text-xs text-neutral-500">Paid</p>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-xl">
            <p className="text-xl sm:text-2xl font-bold text-blue-600">{invoiceStats.pending || 0}</p>
            <p className="text-[10px] sm:text-xs text-neutral-500">Pending</p>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-xl">
            <p className="text-xl sm:text-2xl font-bold text-red-600">{invoiceStats.overdue || 0}</p>
            <p className="text-[10px] sm:text-xs text-neutral-500">Overdue</p>
          </div>
          <div className="text-center p-3 bg-white/40 rounded-xl col-span-2 sm:col-span-1">
            <p className="text-xl sm:text-2xl font-bold text-neutral-400">{invoiceStats.cancelled || 0}</p>
            <p className="text-[10px] sm:text-xs text-neutral-500">Cancelled</p>
          </div>
        </div>
      </div>
    </div>
  );
}
